import { useEffect, useRef, useState } from "react";
import type { RefetchOptions, UseQueryResult } from "react-query";
import { useMutation, useQuery, useQueryClient } from "react-query";

import isEqual from "lodash/isEqual";

import type {
  Categories,
  GroupedByCategoryPurposeDescriptions,
  GroupedPurposes,
  OtPurpose,
  OtPurposeByCategory,
  OtPurposeBySteps,
  OtPurposeByUUID,
  PurposeGrouping,
  Steps,
  Translate,
} from "@jti/fe-common";
import { GlobalEvents } from "@jti/fe-common";

import { MUTATION_KEYS, QUERY_KEYS } from "../../constants/queryKeys";
import { CasCustomAttrsApi } from "../../services/cas/CasCustomAttrsApi";
import { CasPurposesApi } from "../../services/cas/CasPurposesApi";
import { getUser } from "../../utils/User";

export const useOtPurposesByUser = <
  Step extends Steps,
  Result extends (Step extends "all" ? OtPurposeBySteps & OtPurposeByUUID : OtPurpose),
>(
  step: Step
): UseQueryResult<Result[], unknown> => {
  const { uuid } = getUser();
  const getPurposesForUserByStep = async (): Promise<OtPurposeByUUID[]> => {
    const data = await CasPurposesApi.init().getPurposesForUserByStep(step);
    if (!data.length) {
      throw new Error("Failed to fetch from OT");
    }
    return data;
  };

  return useQuery([QUERY_KEYS.consentPurposes, "step", uuid, step], getPurposesForUserByStep);
};

export const useOtPurposesForUserByCategory = <
  Category extends Categories,
  Result extends (Category extends "all" ? OtPurposeByUUID & OtPurposeByCategory : OtPurposeByUUID),
>(
  category: Category,
  enabled = true
): UseQueryResult<Result[]> => {
  const { uuid } = getUser();
  const getPurposesForUserByStep = async (): Promise<OtPurposeByUUID[]> => {
    const data = await CasPurposesApi.init().getPurposesForUserByCategory(category, false);
    if (!data.length) {
      throw new Error("Failed to fetch from OT");
    }
    return data;
  };

  return useQuery([QUERY_KEYS.consentPurposes, "category", uuid, category], getPurposesForUserByStep, { enabled });
};

export const useOtPurposesByStep = <
  Step extends Steps,
  Result extends (Step extends "all" ? OtPurposeBySteps : OtPurpose),
>(
  step: Step,
  isLast = true
): UseQueryResult<Result[], unknown> => {
  const getPurposesByStep = async (): Promise<OtPurpose[]> => {
    const data = await CasPurposesApi.init().getPurposesByStep(step);
    if (!data.length) {
      throw new Error("Failed to fetch from OT");
    }
    return data;
  };
  return useQuery([QUERY_KEYS.consentPurposesByStep, step], getPurposesByStep, { enabled: isLast });
};

export type UpdatePurpose = {
  id: string;
  accepted: boolean;
};

export type UpdatedOtData = {
  consents: UpdatePurpose[];
  preferredLanguage?: string;
};

type UseOtPurposesResponse = {
  purposes: (OtPurposeByUUID & OtPurposeByCategory)[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  groupedPurposes: GroupedPurposes[];
  refetch: (options?: RefetchOptions) => Promise<void>;
};

type UseUpdateConsents = {
  isUpdating: boolean;
  updateConsents: (dataToUpdate: UpdatedOtData) => Promise<void>;
};

const groupPurposeByCategory = (
  purposes: (OtPurposeByUUID & OtPurposeByCategory)[],
  purposesDescriptions?: GroupedByCategoryPurposeDescriptions,
  translate?: Translate
): GroupedPurposes[] => {
  const __ = translate ?? ((key: string) => key);
  const groupedPurposes = purposes.reduce((acc: GroupedPurposes[], currentPurpose) => {
    const matchedCategoryPurpose = acc.find((category) => category.category === currentPurpose.category);
    const preparedPurposeData = {
      ...currentPurpose,
      purposeName: currentPurpose.description,
      purposeDescription: __(`profile.communication.manage_consent.${currentPurpose.category}.label`),
    };

    if (matchedCategoryPurpose) {
      matchedCategoryPurpose.purposes.push(preparedPurposeData);
    } else {
      acc.push({
        purposeTitle: __(`profile.communication.manage_consent.${currentPurpose.category}.title`),
        purposeDescription: purposesDescriptions?.[currentPurpose.category],
        purposeId: currentPurpose.name,
        category: currentPurpose.category,
        purposes: [preparedPurposeData],
      });
    }
    return acc;
  }, []);
  const orderOfGroups = ["email", "sms", "post", "survey", "additional", "GDPR"];
  return groupedPurposes.sort(
    (compareCategoryFirst, compareCategorySecond) =>
      orderOfGroups.indexOf(compareCategoryFirst.category) - orderOfGroups.indexOf(compareCategorySecond.category)
  );
};

const groupPurposesBySection = (purposes: (OtPurposeByUUID & OtPurposeByCategory)[]): GroupedPurposes[] => {
  const sectionGroupedPurposes = purposes.reduce((acc: GroupedPurposes[], currentPurpose) => {
    const sectionPurpose = acc.find((purpose) => purpose.purposeId === currentPurpose.section.id);
    const preparedPurposeData = {
      ...currentPurpose,
      purposeName: currentPurpose.name,
      purposeDescription: currentPurpose.description,
    };

    if (sectionPurpose) {
      sectionPurpose.purposes.push(preparedPurposeData);
    } else {
      acc.push({
        purposeTitle: currentPurpose.section.name,
        purposeDescription: currentPurpose.section.description,
        purposeId: currentPurpose.section.id,
        category: currentPurpose.category,
        purposes: [preparedPurposeData],
      });
    }
    return acc;
  }, []);

  return sectionGroupedPurposes;
};

const purposesList = {
  category: groupPurposeByCategory,
  section: groupPurposesBySection,
};

export const useOtPurposes = (
  step: Steps,
  groupingType: PurposeGrouping = "category",
  purposesDescriptions?: GroupedByCategoryPurposeDescriptions,
  translate?: Translate,
  enabled = true
): UseOtPurposesResponse => {
  const queryClient = useQueryClient();
  const { uuid } = getUser();

  // NOTE: This effect fires for the cases when the consents are updated elsewhere and we need to refresh the local cache.
  useEffect(() => {
    const refreshConsents = (event: Event) => {
      if ((event as CustomEvent<{ additionalData: { uuid: string } }>).detail?.additionalData?.uuid !== uuid) return;
      void queryClient.invalidateQueries([QUERY_KEYS.consentPurposes, "category", uuid]);
    };

    document.addEventListener(GlobalEvents.ConsentsUpdated, refreshConsents);

    return () => document.removeEventListener(GlobalEvents.ConsentsUpdated, refreshConsents);
  }, [queryClient, uuid]);

  const {
    data: purposesByStep,
    isLoading: isLoadingSteps,
    isFetching: isFetchingSteps,
    isError: isErrorSteps,
    refetch: refetchByStep,
  } = useOtPurposesByStep(step, enabled);
  const {
    data: purposesByUser,
    isLoading: isLoadingUser,
    isFetching: isFetchingUserData,
    isError: isErrorUser,
    refetch: refetchByUser,
  } = useOtPurposesForUserByCategory("all", enabled);
  const [purposes, setPurposes] = useState<(OtPurposeByUUID & OtPurposeByCategory)[]>([]);
  const [groupedPurposes, setGroupedPurposes] = useState<GroupedPurposes[]>([]);
  const prevGroupedPurposesRef = useRef<GroupedPurposes[]>([]);

  useEffect(() => {
    if (purposesByStep && purposesByUser) {
      const filteredPurposes = purposesByStep.reduce(
        (purposesAcc: (OtPurposeByUUID & OtPurposeByCategory)[], purposeCurr) => {
          const userPurpose = purposesByUser.find((purposeByUser) => purposeByUser.id === purposeCurr.id);
          if (userPurpose) {
            purposesAcc.push({
              ...userPurpose,
              mandatory: purposeCurr.mandatory,
              section: purposeCurr.section,
            });
          }
          return purposesAcc;
        },
        []
      );
      setPurposes(filteredPurposes);
    }
  }, [isLoadingSteps, isLoadingUser, purposesByStep, purposesByUser]);

  useEffect(() => {
    if (purposes.length > 0) {
      const groupedConsents = purposesList[groupingType](purposes, purposesDescriptions, translate);
      if (!isEqual(groupedConsents, prevGroupedPurposesRef.current)) {
        setGroupedPurposes(groupedConsents);
        prevGroupedPurposesRef.current = groupedConsents;
      }
    }
  }, [purposes, groupingType, purposesDescriptions]);

  const refetchUserConsents = async (options?: RefetchOptions) => {
    await Promise.all([refetchByStep(options), refetchByUser(options)]);
  };

  return {
    purposes,
    groupedPurposes,
    isLoading: isLoadingSteps || isLoadingUser,
    isFetching: isFetchingSteps || isFetchingUserData,
    isError: isErrorSteps || isErrorUser,
    refetch: refetchUserConsents,
  };
};

export const useUpdateConsents = (): UseUpdateConsents => {
  const updatePurposes = useMutation(
    MUTATION_KEYS.updateConsentPurposes,
    ({ consents, preferredLanguage }: UpdatedOtData) =>
      CasCustomAttrsApi.init().writeConsents({
        consents,
        preferredLanguage,
      })
  );

  const updateConsents: UseUpdateConsents["updateConsents"] = async (dataToUpdate) => {
    await updatePurposes.mutateAsync(dataToUpdate);
  };

  return { updateConsents, isUpdating: updatePurposes.isLoading };
};
